// Neo-fintech story primitives — the modern replacement for the dawn SceneShell /
// SpeechBubble / ChoiceCard. Same narrative beats (a coach speaks, the player taps
// through lines, then picks a card), now in the same visual language as the HQ,
// trading floor, and venture screens: themed tokens, light + dark, clean cards.

import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Eyebrow } from '../ui/kit.js';
import { ThemeToggle } from '../ui/theme.js';
import { CartoonMiko } from '../ui/cartoon.js';
import { useVoice } from '../voice/VoiceContext.js';

/** Tone → accent colour, shared by coach avatar + choice cards. */
export type StoryTone = 'fair' | 'warm' | 'premium' | 'neutral';
export const toneColor: Record<StoryTone, string> = {
  fair: 'var(--brand)',
  warm: 'var(--up)',
  premium: 'var(--info)',
  neutral: 'var(--text-3)',
};

/** Full-screen, opaque, themed shell. Replaces the dawn SceneShell (which floated
 *  over the painterly World). A centred column keeps the story readable. */
export function StoryShell({
  eyebrow,
  children,
  footer,
  maxWidth = 560,
  backdrop,
}: {
  eyebrow?: string;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
  /** When 'studio', layer a saturated studio glow behind the story for that
   *  big-game first-impression feel. Defaults off for utility screens. */
  backdrop?: 'voxel-sky' | 'studio';
}) {
  return (
    <motion.div
      className="bk-app"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ position: 'fixed', inset: 0, zIndex: 6, overflowY: 'auto' }}
    >
      {backdrop && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(600px 360px at 85% 0%, rgba(52,200,255,0.35), transparent 60%), radial-gradient(560px 360px at 10% 10%, rgba(255,92,200,0.28), transparent 58%)',
          }}
        />
      )}
      <div
        style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          maxWidth,
          margin: '0 auto',
          padding: 'clamp(16px, 4vw, 32px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          {eyebrow ? <Eyebrow color="var(--brand)">{eyebrow}</Eyebrow> : <span />}
          <ThemeToggle />
        </header>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16, paddingBottom: 24 }}>
          {children}
        </div>
        {footer && <div style={{ paddingBottom: 12 }}>{footer}</div>}
      </div>
    </motion.div>
  );
}

/** The coach (Miko) speaking. Taps advance through `lines`; after the last line,
 *  `onDone` fires. Replaces SpeechBubble — same tap-to-continue contract. */
export function Coach({
  lines,
  onDone,
  speaker = 'Miko',
}: {
  lines: string[];
  onDone: () => void;
  speaker?: string;
}) {
  const [i, setI] = useState(0);
  const last = i >= lines.length - 1;
  const next = () => (last ? onDone() : setI((n) => n + 1));

  // MIKO SPEAKS — read each story line aloud as it appears (and on mount), so
  // early readers can play the chapter by ear. Respects the voice toggle;
  // tapping to the next line cancels the previous one mid-sentence (speak()
  // never stacks utterances).
  const { speak } = useVoice();
  const line = lines[i];
  useEffect(() => {
    if (line) void speak(line);
  }, [line, speak]);

  return (
    <Card pad={0} style={{ overflow: 'hidden' }}>
      <button
        type="button"
        onClick={next}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 18,
          cursor: 'pointer',
          fontFamily: 'inherit',
          color: 'var(--text)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: 12, flexShrink: 0,
              background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand) 22%, var(--surface)), var(--surface-2))',
              boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.20)',
              display: 'grid', placeItems: 'center',
            }}
          >
            <CartoonMiko size={44} headphones />
          </div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 14, color: 'var(--text)' }}>{speaker}</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 700 }}>Your mentor</div>
          </div>
        </div>
        <motion.p
          key={i}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ margin: 0, fontSize: 17, lineHeight: 1.55, color: 'var(--text)', fontWeight: 500 }}
        >
          {lines[i]}
        </motion.p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {lines.length > 1 &&
              lines.map((_, n) => (
                <span
                  key={n}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: n <= i ? 'var(--brand)' : 'var(--surface-3)',
                  }}
                />
              ))}
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 900, color: 'var(--brand)' }}>
            {last ? "LET'S GO →" : 'TAP →'}
          </span>
        </div>
      </button>
    </Card>
  );
}

/** A choice the player can pick. Replaces ChoiceCard — label, optional subtitle,
 *  and an optional preview block, with a tone accent down the left edge. */
export function StoryChoice({
  tone = 'neutral',
  label,
  subtitle,
  preview,
  onClick,
}: {
  tone?: StoryTone;
  label: string;
  subtitle?: string;
  preview?: ReactNode;
  onClick: () => void;
}) {
  const accent = toneColor[tone];
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ y: 1, boxShadow: `0 0 0 1px ${accent}66, 0 0 8px ${accent}55` }}
      style={{
        width: '100%',
        textAlign: 'left',
        background: '#241b42',
        border: `2.5px solid ${accent}`,
        borderLeft: `8px solid ${accent}`,
        borderRadius: 16,
        boxShadow: `0 0 0 1px ${accent}55, 0 0 16px ${accent}44, 0 6px 16px rgba(0,0,0,0.5)`,
        padding: 16,
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'grid',
        gap: preview ? 10 : 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontWeight: 900, fontSize: 17, color: 'var(--text)' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: accent }}>Choose →</span>
      </div>
      {subtitle && <span style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5 }}>{subtitle}</span>}
      {preview && <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>{preview}</div>}
    </motion.button>
  );
}

/** Small key/value preview rows for a choice card. */
export function PreviewRows({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {rows.map(([k, v], idx) => (
        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
          <span style={{ color: 'var(--text-3)' }}>{k}</span>
          <span className="bk-tnum" style={{ fontWeight: 800, color: 'var(--text)' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

/** Tiny inline style helper for choice-grid layouts. */
export const choiceGrid: CSSProperties = { display: 'grid', gap: 12 };
